import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid, } from 'uuid';
import Confirm from '../../confirm/Confirm';
import FieldSet from '../../fieldset/FieldSet';
import { buildFailureStatus, buildSuccessStatus, } from '../../constants/AlertConstants';
import AsyncActionButton from '../../button/AsyncActionButton';
import { V2, V3, } from '../../../modules/Constants';
import RefundStrategy from './RefundStrategy';
import { MONEY_REFUND, STANDARD_REFUND, REFUND_SERVICE_REFUND, } from './Constants';
import PaymentGatewayService from '../../../service/PaymentGatewayService';

class PartialRefundOperation extends React.Component {
  constructor(props) {
    super(props);
    const inputs = [];
    const { data, } = this.props;
    const { transaction, } = data;
    let isAddInputHidden = false;

    if (transaction.version === V3) {
      isAddInputHidden = true;
      transaction.lineItems.forEach((item) => {
        inputs.push({
          key: uuid(),
          billId: item._id,
          money: item.amount.replace(/\$/g, ''),
          immutable: true,
        });
      });
    }

    this.state = {
      isLoading: false,
      inputs,
      status: null,
      isConfirmationOpen: false,
      isAddInputHidden,
      refundReasons: [],
    };
  }

  async componentDidMount() {
    await this.getRefundReasons();
  }

  async getRefundReasons() {
    await PaymentGatewayService.refund.getRefundReasons()
      .then(response => {
        const { refundReasons, } = response.data;
        const tempRefundReasons = [];
        Object.entries(refundReasons).map(([ key, val, ]) => (
          tempRefundReasons.push({ id: key, name: val.name, description: val.description, })
        ));
        this.setState({
          refundReasons: tempRefundReasons,
        });
      });
  }

  onDismiss() {
    return () => {
      this.setState({
        status: null,
      });
    };
  }

  onConfirmationOpen() {
    this.setState({
      isConfirmationOpen: true,
    });
  }

  onConfirmationClose() {
    this.setState({
      isConfirmationOpen: false,
    });
  }

  onBillIDChange(payableElement) {
    const { inputs = [], } = this.state;
    const record = inputs.find(input => input.key === payableElement.key);
    record.billId = payableElement.value;
    this.setState({
      inputs,
    });
  }

  onMoneyChange(payableElement) {
    const { inputs = [], } = this.state;
    const record = inputs.find(input => input.key === payableElement.key);
    record.money = payableElement.value;
    this.setState({
      inputs,
    });
  }

  onNoteChange(payableElement) {
    const { inputs = [], } = this.state;
    const record = inputs.find(input => input.key === payableElement.key);
    record.note = payableElement.value;
    this.setState({
      inputs,
    });
  }

  onReasonChange(payableElement) {
    const { inputs = [], } = this.state;
    const record = inputs.find(input => input.key === payableElement.key);
    record.reason = payableElement.value;
    this.setState({
      inputs,
    });
  }

  onSuccessfulRefund(data, version) {
    if (version === V2) {
      const { chargedAmount = {}, } = data;
      this.setState({
        status: buildSuccessStatus(
          ` Refunded transaction id: ${data._id} for ${chargedAmount.amount} ${
            chargedAmount.currencyCode
          }`,
          this.onDismiss()
        ),
      });
    } else {
      const { refund, } = data;
      this.setState({
        status: buildSuccessStatus(` Refunded transaction id: ${refund._id || refund.refundTransaction._id}`, this.onDismiss()),
      });
    }
  }

  formatFees(fees) {
    const obj = {};
    Object.keys(fees).forEach((key) => {
      const fee = fees[key];
      obj[key] = fee;
      obj[key].amount = fee.value;
      obj[key].currencyCode = fee.currencyCode;
      delete obj[key].value;
    });
    return obj;
  }

  formatPayload(transaction, inputs) {
    const { data, } = this.props;
    const v3Filter = item => inputs.map(input => input.billId).includes(item._id);
    const v3Map = (item) => {
      const formatted = { ...item, };
      const input = inputs.find(inputValue => inputValue.billId === formatted._id);
      formatted.fees = formatted.fees ? this.formatFees(formatted.fees) : {};
      formatted.amount = {
        amount: input.money,
        currencyCode: 'USD',
      };
      return formatted;
    };
    if (transaction.version === V2 && data.refundMethod === STANDARD_REFUND) {
      return {
        transactionId: transaction.id,
        billRefundMap: inputs.reduce((acc, currentValue) => {
          acc[currentValue.billId] = {
            amount: currentValue.money,
            currencyCode: 'USD',
          };
          return acc;
        }, {}),
      };
    }
    const isValidMethod = [ STANDARD_REFUND, MONEY_REFUND, REFUND_SERVICE_REFUND, ].includes(data.refundMethod);
    if (transaction.version === V3 && isValidMethod) {
      return {
        transactionId: transaction.id,
        lineItems: transaction.lineItems.filter(v3Filter).map(v3Map),
        fees: {},
        reason: inputs[0].reason,
        note: inputs[0].note,
      };
    }

    if (transaction.version === V2 && data.refundMethod === MONEY_REFUND) {
      const [ input = {}, ] = inputs;
      return {
        transactionId: transaction.id,
        refundRequest: {
          customerId: transaction.customerId,
          serviceOfferingId: transaction.serviceOfferingId,
          totalAmount: {
            amount: input.money,
            currencyCode: 'USD',
          },
          serviceFeeAmount: {
            amount: '0.00',
            currencyCode: 'USD',
          },
        },
      };
    }
    return {};
  }

  refund(transaction, inputs = []) {
    const { data, } = this.props;
    this.setState({ isLoading: true, status: null, });

    const payload = this.formatPayload(transaction, inputs);

    if (payload.lineItems === 0) {
      throw Error('Prevent full refund');
    }

    new RefundStrategy.Builder()
      .version(transaction.version)
      .refundMethod(data.refundMethod)
      .refundType(data.refundType)
      .payload(payload)
      .build()
      .refund()
      .then((response) => {
        this.onSuccessfulRefund(response.data, transaction.version);
      })
      .catch(({ response, }) => {
        const errors = response.data.errors.map(error => error.message);
        this.setState({
          status: buildFailureStatus(
            <div>
              <p>{`Unable to refund ${transaction.id}`}</p>
              {errors.map((e, index) => (
                <p key={`error-$${index}`}>{e}</p>
              ))}
            </div>,
            this.onDismiss()
          ),
        });
      })
      .finally(() => {
        this.setState({ isLoading: false, });
        this.onConfirmationClose();
      });
  }

  addInput() {
    const { inputs = [], } = this.state;
    inputs.push({
      key: 'billAndMoneyInput',
      billId: '',
      money: '',
      reason: '',
      note: '',
    });

    this.setState({
      inputs,
    });
  }

  removeInput(inputparam) {
    const { inputs = [], } = this.state;
    const filteredInput = inputs.filter(input => input.key !== inputparam.key);
    this.setState({
      inputs: filteredInput,
    });
  }

  formatInputs(inputs, refundReasons) {
    return inputs.map((input) => {
      const billElementId = `${input.key} - billId`;
      const moneyElementId = `${input.key} - money`;
      const noteElementId = `${input.key} - note`;
      const reasonElementId = `${input.key} - reason`;

      return (
        <div className="mt-3 mb-3 bg-light" key='billAndMoneyInputField'>
          <FieldSet title="">
            <span className="form-label">Payable ID</span>
            <input
              id={billElementId}
              className="form-control"
              type="text"
              readOnly={input.immutable}
              placeholder="Enter the applicable bill ID you are trying to refund"
              value={input.billId}
              onChange={e => this.onBillIDChange({
                key: input.key,
                value: e.target.value,
              })}
            />
            <span className="form-label">
              Partial Amount
              <strong>(in dollars)</strong>
            </span>
            <input
              id={moneyElementId}
              className="form-control"
              type="text"
              placeholder="Ex: 0.00"
              value={input.money}
              onChange={e => this.onMoneyChange({
                value: e.target.value,
                key: input.key,
              })}
            />
            <span className="form-label">
              Refund Reason
            </span>
            <select
              id={reasonElementId}
              className="form-select"
              value={input.reason ? input.reason : 'REASON_NOT_PROVIDED'}
              onChange={e => this.onReasonChange({
                value: e.target.value,
                key: input.key,
              })}
            >
              {refundReasons.map(refundReason => (
                <option value={refundReason.id} key={refundReason.id}>
                  {refundReason.description}
                </option>
              ))}
            </select>
            <span className="form-label">
              Note (optional)
            </span>
            <input
              id={noteElementId}
              className="form-control"
              type="text"
              placeholder="optional"
              value={input.note}
              onChange={e => this.onNoteChange({
                value: e.target.value,
                key: input.key,
              })}
            />
            <button
              type="button"
              className="btn btn-danger mt-2"
              onClick={() => this.removeInput(input)}
            >
              Remove
            </button>
          </FieldSet>
        </div>
      );
    });
  }

  render() {
    const {
      isLoading, isConfirmationOpen, inputs, status, isAddInputHidden, refundReasons,
    } = this.state;
    const { data, back, reset, } = this.props;
    const { transaction, } = data;
    return (
      <div>
        <div className="container">
          {data.getDetails()}
          {status}
          <div className="small">
            <div className="row">
              <div className="col">
                <button
                  className="btn btn-primary m-1"
                  type="button"
                  hidden={isAddInputHidden}
                  onClick={() => this.addInput()}
                >
                  Add Input
                </button>
                {this.formatInputs(inputs, refundReasons)}
              </div>
            </div>
          </div>
        </div>
        <Confirm
          isLoading={isLoading}
          title="Refund"
          content={`Are you sure you want to want to refund transaction: ${transaction.id}?`}
          open={isConfirmationOpen}
          onClose={() => this.onConfirmationClose()}
          onYes={() => this.refund(transaction, inputs)}
          onNo={() => this.onConfirmationClose()}
        />
        <div className="row justify-content-center mt-3 m-1">
          <button type="button" className="btn btn-light" onClick={() => back()}>
            previous
          </button>
          <div className=" m-1">
            <AsyncActionButton
              isLoading={isLoading}
              onClick={() => this.onConfirmationOpen()}
              contents="submit"
              variant="light"
            />
          </div>
          <button type="button" className="btn btn-light m-1" onClick={() => reset()}>
            reset
          </button>
        </div>
      </div>
    );
  }
}
PartialRefundOperation.propTypes = {
  data: PropTypes.object.isRequired,
  back: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
};
export default PartialRefundOperation;
